import { useEffect, useState } from 'react';
import { Button, Form, Popover, Tooltip } from 'antd';
import { throwError } from '@copilot/common/utils';
import { isEmpty, isNil, isUndefined } from 'lodash';
import { SaveOutlined, InfoCircleFilled, VideoCameraAddOutlined } from '@ant-design/icons';
import { TextEditor } from '../textEditor';
import { TextEditorSmartReply } from '../textEditor/textEditorSmartReply';
import { TextEditorAutomatedStepsAndReminderFooter } from '@copilot/common/components/forms/textEditor/footer/automatedStepsAndReminder';
import { TextEditorScheduledMessageFooter } from '../textEditor/footer/scheduledMessage';
import { MessageFormProps } from './types';
import styles from './messageForm.module.less';
import ResumeAutomationButton from './resumeAutomationButton';
import { THREAD_RESTRICTED_TEXT, VIDEO_IN_APP_VALIDATION_ENABLED_BUTTON_TEXT } from './constants';
import { TEXT_EDITOR_SAVE_QUICK_RESPONSE_TRACKING_ID } from '@copilot/common/tracking/userpilotEventConsts';
import { PromptSubmissionData } from '@copilot/common/hooks/smartReply/smartReplyTypes';

/**
 * [Smart] Message Form
 * Form component to selectively handle message template selection, automation, and submitting/scheduling messages
 * using a template radio selector or template dropdown
 */
function MessageForm({
    messageTemplates,
    automatedSteps = [],
    isDisabled = false,
    isScheduled = false,
    isUsingTemplateDropdown = false,
    disabledText,
    onSetupTemplate,
    onSubmit,
    onScheduledSubmit,
    onSetReminder,
    onCardTracking,
    smartReplyHookData: {
        writeSmartReply,
        regenerateSmartReply,
        editSmartReply,
        canUseCommunicationStyles,
    },
    smartReplyResult,
    isSubmitting,
    isThreadRestricted,
    smartReplyPromptCategory,
    videoInAppValidationEnabled,
    recommendedMessage,
    initialValue = '',
    isReplyRecommendationsEnabled = true,
}: MessageFormProps) {
    const [form] = Form.useForm();
    const [lastUsedPrompt, setLastUsedPrompt] = useState<PromptSubmissionData>();

    const [onTextAreaFocus, setOnTextAreaFocus] = useState<boolean>(false);
    const [isAssistantActive, setAssistantActive] = useState<boolean>(false);
    // #region ai message generation
    const [hasGenerated, setHasGenerated] = useState<boolean>(false);
    useEffect(() => {
        if (!isNil(smartReplyResult?.data) && !hasGenerated) {
            setHasGenerated(true);
            form.setFieldsValue({ message: smartReplyResult.data });
        }
    }, [smartReplyResult.data, hasGenerated, form]);

    // Wrapper for writeSmartReply to set the last used prompt
    function onWriteSmartReply(
        prompt: string,
        message?: string,
        includePersonalizedInsights?: boolean
    ) {
        setLastUsedPrompt({
            promptId: prompt,
            instruction: message,
            actionType: 'WRITE',
        });
        return writeSmartReply(prompt, message, includePersonalizedInsights);
    }

    // Wrapper for editSmartReply to set the last used prompt
    function onEditSmartReply(
        messageToBeEdited: string,
        prompt: string,
        message?: string,
        includePersonalizedInsights?: boolean
    ) {
        setLastUsedPrompt({ promptId: prompt, instruction: message, actionType: 'EDIT' });
        return editSmartReply(messageToBeEdited, prompt, message, includePersonalizedInsights);
    }

    function calcDisabledText() {
        if (isThreadRestricted) return undefined;
        return disabledText;
    }

    function onSignUpToVideo() {
        window.open(
            'https://docs.google.com/forms/d/e/1FAIpQLScOA4_8wL0J2-2nkSW3KGS2JDaaq1PkU4n4c4yGJ9zvktnfEw/viewform',
            '_blank'
        );
    }

    useEffect(() => {
        if (onTextAreaFocus) setAssistantActive(false);
    }, [onTextAreaFocus]);
    // #endregion

    const isAbleToResumeAutomation = automatedSteps.length > 0;

    return (
        <TextEditor
            className={styles.textEditorWrapper}
            value={initialValue}
            recommendedMessage={recommendedMessage}
            disabled={isDisabled}
            isReplyRecommendationsEnabled={isReplyRecommendationsEnabled}
        >
            <TextEditor.Header>
                {!isEmpty(messageTemplates) ? (
                    <TextEditor.Dropdown
                        items={messageTemplates.map((template) => ({
                            key: template.id,
                            label: template.name,
                            value: template.message,
                            title: template.name,
                        }))}
                    >
                        Quick Responses
                    </TextEditor.Dropdown>
                ) : null}
                <Tooltip placement="bottom" title="Save as quick response" trigger="hover">
                    <TextEditor.Button
                        icon={<SaveOutlined />}
                        onClick={onSetupTemplate}
                        style={{ border: 'none', boxShadow: 'none' }}
                        data-tracking-id={TEXT_EDITOR_SAVE_QUICK_RESPONSE_TRACKING_ID}
                    />
                </Tooltip>
                {isAbleToResumeAutomation && (
                    <ResumeAutomationButton
                        automatedSteps={automatedSteps}
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                        disabled={isThreadRestricted || isDisabled}
                        onCardTracking={onCardTracking}
                    />
                )}
            </TextEditor.Header>
            <TextEditor.Body
                placeholder={'Type your message or select from the option above'}
                disabledText={calcDisabledText()}
                size={'default'}
                bordered={false}
                onFocus={() => setOnTextAreaFocus(true)}
                onBlur={() => setOnTextAreaFocus(false)}
            >
                {isThreadRestricted && (
                    <div className={styles.restrictedMessageBanner}>
                        <InfoCircleFilled className={styles.restrictedIcon} />
                        {THREAD_RESTRICTED_TEXT}
                    </div>
                )}
                <div className={styles.bodyButtonWrapper}>
                    <TextEditorSmartReply
                        isActive={isAssistantActive}
                        canRegenerate={hasGenerated}
                        onRegenerate={regenerateSmartReply}
                        setIsActive={setAssistantActive}
                        onWrite={onWriteSmartReply}
                        data={smartReplyResult}
                        onEdit={onEditSmartReply}
                        canUseCommunicationStyles={canUseCommunicationStyles}
                        smartReplyPromptCategory={smartReplyPromptCategory}
                    />
                    {videoInAppValidationEnabled && (
                        <Popover
                            content={
                                <div className={styles.signUpWrapper}>
                                    <div className={styles.recordVideoPopover}>
                                        Do you want 10 personalized business videos? Sign up for our
                                        video BETA test
                                    </div>
                                    <Button size="small" type="primary" onClick={onSignUpToVideo}>
                                        Sign up here
                                    </Button>
                                </div>
                            }
                            trigger="click"
                            placement="topLeft"
                        >
                            <Button disabled={isDisabled} icon={<VideoCameraAddOutlined />}>
                                {VIDEO_IN_APP_VALIDATION_ENABLED_BUTTON_TEXT}
                            </Button>
                        </Popover>
                    )}
                </div>
            </TextEditor.Body>
            <TextEditor.Footer>
                {isScheduled && !isUndefined(onScheduledSubmit) ? (
                    <TextEditorScheduledMessageFooter
                        isSubmitting={isSubmitting}
                        onScheduledSubmit={onScheduledSubmit}
                    />
                ) : (
                    <TextEditorAutomatedStepsAndReminderFooter
                        onSubmit={(text, nodeId, quickResponseId, reminderDate) => {
                            const removeReminder = isNil(reminderDate);
                            const promptData =
                                text === smartReplyResult.data && !isNil(lastUsedPrompt)
                                    ? lastUsedPrompt
                                    : undefined;
                            isUndefined(onSubmit)
                                ? throwError('Missing callback for sending messages')
                                : onSubmit(
                                      text,
                                      removeReminder,
                                      nodeId,
                                      quickResponseId,
                                      promptData
                                  );
                            if (isNil(reminderDate)) return;
                            isUndefined(onSetReminder)
                                ? throwError('Missing callback for setting reminder')
                                : onSetReminder(reminderDate);
                        }}
                        showAutomationSteps={!isUsingTemplateDropdown}
                    />
                )}
            </TextEditor.Footer>
        </TextEditor>
    );
}

export default MessageForm;
